import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabaseMutationFn, useSupabaseTypedClient } from 'hooks/reactQuery';
import { useToastWrapper } from 'hooks/useToastWrapper';

import { useCurrentUser } from 'auth/CurrentUserProvider';
import type { RoastData } from 'features/roast/utils';
import { EdgeFunctionName } from 'types/edgeFunctions';
import { TanstackQueryName } from 'types/frontend';

interface VoteRoastParams {
  roastId: string;
}

interface VoteRoastResponse {
  roast_id: string;
  is_upvoted: boolean;
  upvote_count: number;
}

export const useRoastVoteMutation = () => {
  const supabase = useSupabaseTypedClient();
  const queryClient = useQueryClient();
  const { id: userId } = useCurrentUser();
  const { toastError } = useToastWrapper();

  return useMutation<VoteRoastResponse, string, VoteRoastParams>({
    mutationFn: supabaseMutationFn(({ roastId }) =>
      supabase.functions.invoke<VoteRoastResponse>(EdgeFunctionName.Roast, {
        body: { type: 'vote', roast_id: roastId },
      })
    ),
    onMutate: async ({ roastId }) => {
      // Cancel outgoing refetches so they don't overwrite optimistic update
      await queryClient.cancelQueries({ queryKey: [TanstackQueryName.GetRoastUserVote, roastId, userId] });
      await queryClient.cancelQueries({ queryKey: [TanstackQueryName.GetRoastDetail, roastId] });

      // Snapshot previous values
      const previousHasVoted = queryClient.getQueryData<boolean>(
        [TanstackQueryName.GetRoastUserVote, roastId, userId]
      );
      const previousDetail = queryClient.getQueryData<RoastData>(
        [TanstackQueryName.GetRoastDetail, roastId]
      );

      const wasVoted = previousHasVoted ?? false;
      const delta = wasVoted ? -1 : 1;

      // Optimistically toggle vote state
      queryClient.setQueryData<boolean>(
        [TanstackQueryName.GetRoastUserVote, roastId, userId],
        !wasVoted
      );

      // Optimistically update upvote count on detail
      if (previousDetail) {
        queryClient.setQueryData<RoastData>(
          [TanstackQueryName.GetRoastDetail, roastId],
          { ...previousDetail, upvote_count: Math.max(0, (previousDetail.upvote_count ?? 0) + delta) }
        );
      }

      return { previousHasVoted, previousDetail };
    },
    onError: (error, { roastId }, context) => {
      // Roll back to previous values
      if (context) {
        queryClient.setQueryData(
          [TanstackQueryName.GetRoastUserVote, roastId, userId],
          (context as { previousHasVoted?: boolean }).previousHasVoted
        );
        if ((context as { previousDetail?: RoastData }).previousDetail) {
          queryClient.setQueryData(
            [TanstackQueryName.GetRoastDetail, roastId],
            (context as { previousDetail?: RoastData }).previousDetail
          );
        }
      }
      toastError(error || 'Failed to vote');
    },
    onSettled: (_, __, { roastId }) => {
      // Refetch to ensure server state is in sync
      queryClient.invalidateQueries({ queryKey: [TanstackQueryName.GetRoastUserVote, roastId, userId] });
      queryClient.invalidateQueries({ queryKey: [TanstackQueryName.GetRoastDetail, roastId] });
      queryClient.invalidateQueries({ queryKey: [TanstackQueryName.GetRoasts, userId] });
      queryClient.invalidateQueries({ queryKey: [TanstackQueryName.GetAllRoasts] });
      queryClient.invalidateQueries({ queryKey: [TanstackQueryName.GetProfileRoasts, userId] });
    },
  });
};
